<script lang="ts" setup>
import gsap from 'gsap'
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, useTemplateRef, watch } from 'vue'

import logoSparkSm from '@/assets/svg/logo-spark-sm.svg?url'
import { useAssetPreloader } from '@/composables/useAssetPreloader'
import { CRITICAL_ASSETS, LOADER_LERP_DURATION, MIN_LOADER_DURATION } from '@/constants'
const state = reactive({
  lerpedProgress: 0,
  minDurationPassed: false,
  assetsReady: false
})

const visible = ref(true)
const containerRef = useTemplateRef<HTMLElement>('containerRef')
const logoRef = useTemplateRef<HTMLElement>('logoRef')
const progressBarRef = useTemplateRef<HTMLElement>('progressBarRef')

const { progress, hasErrors, preloadAll } = useAssetPreloader()

const isReady = computed(() => state.assetsReady && state.minDurationPassed)

let entryTimeline: gsap.core.Timeline | null = null
let exitTimeline: gsap.core.Timeline | null = null
const progressLerp = gsap.quickTo(state, 'lerpedProgress', { duration: LOADER_LERP_DURATION, ease: 'power2.out' })

watch(progress, (v) => progressLerp(v))
watch(isReady, (ready) => ready && animateOut(), { once: true })

async function animateOut () {
  await nextTick()
  if (!logoRef.value || !progressBarRef.value || !containerRef.value) {
    visible.value = false
    return
  }

  entryTimeline?.kill()
  exitTimeline?.kill()

  exitTimeline = gsap.timeline({
    onComplete: () => {
      // scrollLocked.value = false
      visible.value = false
    }
  })
    .to(logoRef.value, { scale: 1.1, opacity: 0, duration: 0.5, ease: 'power2.in' }, 0)
    .to(progressBarRef.value, { opacity: 0, y: 10, duration: 0.3, ease: 'power2.in' }, 0)
    .to(containerRef.value, { opacity: 0, duration: 0.4, ease: 'power2.inOut' }, 0.2)
}

async function animateIn () {
  await nextTick()
  if (!logoRef.value || !progressBarRef.value) return

  entryTimeline?.kill()

  gsap.set(logoRef.value, { scale: 0.8 })
  gsap.set(progressBarRef.value, { opacity: 0, y: 20 })

  entryTimeline = gsap.timeline()
    .to(logoRef.value, { scale: 1, duration: 0.8, ease: 'back.out(1.2)' })
    .to(progressBarRef.value, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, '-=0.3')
}

async function simulateProgress () {
  const steps = [30, 60, 85, 100]
  for (const step of steps) {
    progressLerp(step)
    await new Promise(resolve => setTimeout(resolve, MIN_LOADER_DURATION / steps.length))
  }
}

onBeforeUnmount(() => {
  progressLerp.tween.kill()
  entryTimeline?.kill()
  exitTimeline?.kill()
})

onMounted(async () => {
  // scrollLocked.value = true
  animateIn()

  const minDurationPromise = new Promise<void>(resolve => setTimeout(() => { state.minDurationPassed = true; resolve() }, MIN_LOADER_DURATION))
  const preloadPromise = (async () => {
    if (CRITICAL_ASSETS.length === 0) await simulateProgress()
    else await preloadAll(CRITICAL_ASSETS)
    state.assetsReady = true
  })()

  await Promise.all([minDurationPromise, preloadPromise])
  if (hasErrors.value) console.warn('[Preloader] Some assets failed to load')
})

defineExpose({ visible })
</script>

<template>
  <Transition name="preloader-fade">
    <div v-if="visible" id="app-loading-container">
      <div id="app-loading" ref="containerRef">
        <div class="preloader-bg" />

        <div class="preloader-content">
          <div ref="logoRef" class="logo-container">
            <img :src="logoSparkSm" alt="Logo Spark Small">
          </div>

          <div
            ref="progressBarRef"
            class="progress-container"
            role="progressbar"
            :aria-valuenow="Math.round(state.lerpedProgress)"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div class="progress-track">
              <div class="progress-fill" :style="{ width: `${state.lerpedProgress}%` }" />
            </div>
            <span class="progress-text">{{ Math.round(state.lerpedProgress) }}%</span>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.preloader-fade-leave-active {
  transition: opacity 0.4s ease-out;
}

.preloader-fade-leave-to {
  opacity: 0;
}

#app-loading-container {
  z-index: 9999;

  @apply fixed inset-0 pointer-events-auto overscroll-none overflow-hidden;
}

#app-loading {
  @apply absolute inset-0 flex flex-col items-center justify-center bg-white pointer-events-auto;
}

.preloader-bg {
  @apply absolute inset-0 overflow-hidden;

  &::before,
  &::after {
    @apply absolute w-[60vmax] h-[60vmax] rounded-full opacity-15;

    content: '';
    filter: blur(60px);
  }

  &::before {
    @apply -top-[30%] -left-[20%];

    background: radial-gradient(circle, #000 0%, transparent 70%);
  }

  &::after {
    @apply -bottom-[30%] -right-[20%];

    background: radial-gradient(circle, #000 0%, transparent 70%);
  }
}

.preloader-content {
  @apply relative z-9999 flex flex-col items-center gap-12;
}

.logo-container {
  @apply w-[clamp(80px,15vw,120px)] h-auto;

  will-change: transform;

  svg {
    @apply block w-full h-auto;
  }
}

.progress-container {
  @apply flex flex-col items-center gap-3;
}

.progress-track {
  @apply relative w-[clamp(100px,20vw,150px)] h-[3px] overflow-hidden rounded-sm;

  background: rgb(0 0 0 / 8%);
}

.progress-fill {
  @apply absolute top-0 left-0 h-full rounded-sm;

  background: #000;
  box-shadow: 0 0 8px rgb(0 0 0 / 30%);
  will-change: width;
}

.progress-text {
  @apply text-xs font-medium tracking-widest;

  color: rgb(0 0 0 / 50%);
  font-family: system-ui, -apple-system, sans-serif;
  font-variant-numeric: tabular-nums;
}

@media (prefers-reduced-motion: reduce) {
  .preloader-fade-leave-active {
    transition: opacity 0.1s linear;
  }

  .preloader-bg::before,
  .preloader-bg::after {
    display: none;
  }
}
</style>
